import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router";

import {
  createBacklogItem,
  getBacklogMeta,
  listAllBacklogItems,
  updateBacklogContent,
  updateBacklogItem,
} from "@/api/backlog";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Field, Input, Select } from "@/components/form";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { MutationError, RefreshFailed, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import { Button, FormActionBar, FormPlaceholder, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

/**
 * Create or edit one backlog item.
 *
 * Saving an existing item can touch two different resources — the issue
 * (title, body, open/closed) and the Project fields — so the save runs both
 * calls and only invalidates once. They are not a transaction: if the field
 * update fails after the content update succeeded, the issue keeps its new
 * text and the error is surfaced. That is the honest behaviour given GitHub
 * offers no way to make the pair atomic.
 */
export function BacklogItemEditPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !itemId;

  const metaQuery = useQuery({
    queryKey: ["backlog", "meta"],
    queryFn: () => getBacklogMeta(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  // The list is already cached by the Backlog page and there is no
  // single-item GET — reading the item out of the list keeps the API surface
  // smaller and the page instant when arriving from the table.
  const itemsQuery = useQuery({
    queryKey: ["backlog", "items"],
    queryFn: () => listAllBacklogItems(token),
    enabled: !!token && !isNew,
    staleTime: 30 * 1000,
  });

  const existing = itemsQuery.data?.find((candidate) => candidate.itemId === itemId);

  const [repo, setRepo] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("");
  const [area, setArea] = useState("");
  const [status, setStatus] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isNew || !existing || loaded) return;
    setRepo(existing.repository);
    setTitle(existing.title);
    setBody(existing.body ?? "");
    setPriority(existing.priority ?? "");
    setArea(existing.area ?? "");
    setStatus(existing.status ?? "");
    setLoaded(true);
  }, [existing, isNew, loaded]);

  const fields = metaQuery.data?.fields ?? {};
  const repos = metaQuery.data?.repos ?? [];

  useEffect(() => {
    if (isNew && !repo && repos.length) setRepo(repos[0]);
  }, [isNew, repo, repos]);

  // A new item is open from the moment its repo has its default (the first repo of the Project, set
  // just above); an existing one when its fields hold the item (``loaded``). Dirty = what the form
  // shows differs from what it was opened with, and the way out of the page is held while it is.
  const { dirty, reset: markSaved } = useDirtyValues(
    { repo, title, body, priority, area, status },
    isNew ? repo !== "" || (metaQuery.isSuccess && repos.length === 0) : loaded,
  );
  const guard = useUnsavedChanges(dirty);
  // Closing (or reopening) asks first; only one of the two actions has a failure on screen at a time.
  const [confirmToggle, setConfirmToggle] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return createBacklogItem(token, {
          repo,
          title,
          body,
          ...(priority ? { priority } : {}),
          ...(area ? { area } : {}),
          ...(status ? { status } : {}),
        });
      }
      await updateBacklogContent(token, itemId!, { title, body });
      const changed = {
        ...(priority && priority !== existing?.priority ? { priority } : {}),
        ...(area && area !== existing?.area ? { area } : {}),
        ...(status && status !== existing?.status ? { status } : {}),
      };
      // No-op guard: PATCH /backlog/items rejects an empty body with a 400,
      // which is the right contract for the API but would surface here as a
      // spurious error whenever only the text changed.
      if (Object.keys(changed).length) {
        await updateBacklogItem(token, itemId!, changed);
      }
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backlog", "items"] });
      markSaved();
      guard.leave("/backlog");
    },
  });

  const toggleState = useMutation({
    mutationFn: () =>
      updateBacklogContent(token, itemId!, {
        state: existing?.state === "closed" ? "open" : "closed",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backlog", "items"] });
      setConfirmToggle(false);
      // The operator was told, in the confirmation, what happens to an unsaved edit.
      guard.leave("/backlog");
    },
  });

  const closing = existing?.state !== "closed";
  function saveItem() {
    toggleState.reset();
    save.mutate();
  }
  // Closing is confirmed: it takes the issue out of the active backlog and off the operator's
  // screen (the page goes back to the list). Reopening puts it back and is undone by closing again,
  // so it runs at once — unless the form holds unsaved edits, which leaving for the list would
  // discard: then it asks too, and says so.
  function askToggle() {
    save.reset();
    if (closing || dirty) setConfirmToggle(true);
    else toggleState.mutate();
  }

  // The title comes from the address, so loading and failing both keep it: the same header is
  // drawn by every state of the page (the item's reference only exists once it has loaded).
  const header = (
    <PageHeader
      back={{ to: "/backlog", label: "Backlog" }}
      title={isNew ? "New backlog item" : "Edit item"}
      subtitle={
        existing && (
          <>
            <a
              href={existing.url}
              target="_blank"
              rel="noreferrer"
              className="text-brand-600 hover:underline"
            >
              {existing.repository}#{existing.number}
            </a>{" "}
            — {existing.state === "closed" ? "closed" : "open"}
          </>
        )
      }
    />
  );
  if (isLoadingOrPaused(metaQuery) || (!isNew && isLoadingOrPaused(itemsQuery))) {
    return <FormPlaceholder header={header} />;
  }
  // Only an error with nothing to show replaces the page (see RefreshFailed).
  // The item's own query counts too: without it a failed load left the edit
  // form blank, and saving it would overwrite the issue's body with nothing.
  const failure =
    loadFailure(metaQuery) ?? (!isNew ? loadFailure(itemsQuery) : undefined);
  if (failure) return <FormPlaceholder header={header} failure={failure} />;
  if (!isNew && itemsQuery.data && !existing) {
    return (
      <FormPlaceholder
        header={header}
        failure={{
          error: new Error(`Item ${itemId} not found in the Project`),
          onRetry: () => void itemsQuery.refetch(),
          retrying: itemsQuery.isFetching,
        }}
      />
    );
  }

  const canSave = title.trim().length > 0 && (!isNew || repo.length > 0);

  return (
    <Page width="form">
      <RefreshFailed query={metaQuery} className="mb-4" />
      {!isNew && <RefreshFailed query={itemsQuery} className="mb-4" />}
      {header}

      <div className="card space-y-4 p-4">
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Repo"
            help={
              !isNew
                ? "An issue's repo can't be changed — it would need to be recreated."
                : undefined
            }
          >
            <Select
              value={repo}
              disabled={!isNew}
              onChange={(event) => setRepo(event.target.value)}
              options={repos.map((value) => ({ value, label: value }))}
            />
          </Field>
          <Field label="Title">
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {(
            [
              ["Priority", fields.Priority ?? [], priority, setPriority],
              ["Area", fields.Area ?? [], area, setArea],
              ["Status", fields.Status ?? [], status, setStatus],
            ] as const
          ).map(([label, options, value, setValue]) => (
            <Field key={label} label={label}>
              <Select
                value={value}
                onChange={(event) => setValue(event.target.value)}
                options={[
                  { value: "", label: "—" },
                  ...options.map((option) => ({ value: option, label: option })),
                ]}
              />
            </Field>
          ))}
        </div>

        <div>
          <span className="mb-1 block text-sm font-medium text-ink">
            Body (markdown)
          </span>
          <MarkdownEditor value={body} onChange={setBody} />
        </div>

      </div>

      <FormActionBar
        dirty={dirty}
        submitLabel="Save"
        pending={save.isPending}
        disabled={!canSave}
        disabledReason={isNew ? "A repo and a title are required." : "A title is required."}
        onSubmit={saveItem}
        onCancel={() => navigate("/backlog")}
        error={
          <>
            <MutationError mutation={save} title="Could not save the item" />
            {!confirmToggle && (
              <MutationError
                mutation={toggleState}
                title={closing ? "Could not close the item" : "Could not reopen the item"}
              />
            )}
          </>
        }
      >
        {!isNew && existing && (
          <Button variant="ghost" loading={toggleState.isPending} disabled={save.isPending} onClick={askToggle}>
            {closing ? "Close item" : "Reopen"}
          </Button>
        )}
      </FormActionBar>

      {confirmToggle && existing && (
        <ConfirmModal
          title={closing ? "Close this item?" : "Reopen this item?"}
          confirmLabel={closing ? "Close item" : "Reopen"}
          pendingLabel={closing ? "Closing…" : "Reopening…"}
          pending={toggleState.isPending}
          error={toggleState.error}
          onCancel={() => {
            toggleState.reset();
            setConfirmToggle(false);
          }}
          onConfirm={() => toggleState.mutate()}
        >
          <p>
            {closing
              ? `${existing.repository}#${existing.number} will be closed and taken out of the active backlog. You can reopen it later.`
              : `${existing.repository}#${existing.number} will be reopened and come back to the active backlog.`}
          </p>
          {dirty && (
            <p>
              You have unsaved changes on this form: they will be discarded, and you will be taken back to
              the backlog.
            </p>
          )}
        </ConfirmModal>
      )}
    </Page>
  );
}
